import { lazy, Suspense, useEffect, useLayoutEffect, type ComponentType } from 'react'
import { BrowserRouter, Link, Navigate, Route, Routes, useLocation, useNavigationType } from 'react-router-dom'
import { MotionConfig } from 'motion/react'
import { OfflineBanner } from './components/OfflineBanner'
import { SignInHost } from './components/SignIn'
import { useSignInGate } from './lib/signin'
import Browse from './screens/Browse'
import { FirstRunGate } from './ui/FirstRunGate'
import { reloadOnce } from './lib/chunkReload'
import { f2 } from './copyFlow2'
import { Page } from './ui/Page'
import { ScreenLoading } from './ui/Spinner'
import { endQa, keepQaKey, qaState, qaVerified, useFlag, useQa } from './lib/flags'
import { watchInstallPrompt } from './lib/install'
import { SUPABASE_ANON_KEY, SUPABASE_URL, fn } from './config'

// The plan screens (menu, itinerary, shared plan) load on demand. They are built alongside this shell, so they are
// found by glob: the app builds and routes before each one exists, and picks it up as soon as it does.
const PLAN_SCREENS = import.meta.glob<{ default: ComponentType }>(['./screens/SharedPlan.tsx'])
function NotYet() {
  return (
    <Page className="pt-24">
      <Link to="/saved" className="press t-control inline-flex min-h-11 items-center text-accent-ink">{f2('nav.home')}</Link>
    </Page>
  )
}
// R-70 review layer: ?review=<key> keeps the founder's review key on this device, ?review=off forgets it; either way the
// parameter leaves the address bar. The layer's code is only downloaded while a key is kept.
const ReviewLayer = lazy(reloadOnce(() => import('./review/ReviewLayer')))
function keptReviewKey(): string | null {
  try {
    const u = new URL(location.href)
    const v = u.searchParams.get('review')
    if (v !== null) {
      if (v && v !== 'off') localStorage.setItem('magellan-review', v)
      else localStorage.removeItem('magellan-review')
      u.searchParams.delete('review')
      history.replaceState(history.state, '', u.pathname + u.search + u.hash)
    }
    return localStorage.getItem('magellan-review')
  } catch {
    return null
  }
}
const REVIEW_KEY = keptReviewKey()

// Features 10/16, the hidden QA panel: ?qa=<key> keeps the key on this device (?qa=off forgets it and every override);
// the `qa` function checks it once per browser session. Only then is the panel's code downloaded and its badge shown;
// a wrong key is forgotten. Flags and presets live in lib/flags.ts.
const QAPanel = lazy(reloadOnce(() => import('./components/QAPanel')))
const QA_KEY = keepQaKey()
function useQaGate() {
  useEffect(() => {
    if (!QA_KEY || qaState().on) return
    let live = true
    fetch(`${SUPABASE_URL}/functions/v1/${fn('qa')}`, {
      method: 'POST',
      headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${SUPABASE_ANON_KEY}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ key: QA_KEY }),
    }).then((r) => { if (!live) return; if (r.ok) qaVerified(QA_KEY); else if (r.status === 401) endQa() }).catch(() => { /* offline: try on the next load */ })
    return () => { live = false }
  }, [])
  return useQa().on
}
watchInstallPrompt()

const screen = (name: string) => lazy(reloadOnce(() => PLAN_SCREENS[`./screens/${name}.tsx`]?.() ?? Promise.resolve({ default: NotYet })))
const SharedPlan = screen('SharedPlan')

// F5 (For you under 3 s on mobile data): only Browse (the front page) is in the first download; every other screen is
// its own file, fetched when it is opened. Once the front page is up, the screens people open next are fetched in the
// background so a tap never waits on the network. A file missing after a deploy reloads the page once (lib/chunkReload).
type Mod = Record<string, unknown>
const named = <M extends Mod>(load: () => Promise<M>, key: keyof M & string) => lazy(() => load().then((m) => ({ default: m[key] as ComponentType })))
const WARM = [() => import('./screens/Category'), () => import('./screens/Saved'), () => import('./screens/Build'), () => import('./screens/ItineraryDay'), () => import('./screens/FirstRun'), () => import('./screens/Settings')]
const loadCategory = reloadOnce(() => import('./screens/Category'))
const loadSaved = reloadOnce(() => import('./screens/Saved'))
const loadBuild = reloadOnce(() => import('./screens/Build'))
const loadItinerary = reloadOnce(() => import('./screens/ItineraryDay'))
const loadFirstRun = reloadOnce(() => import('./screens/FirstRun'))
const loadSettings = reloadOnce(() => import('./screens/Settings'))
const Taste = lazy(reloadOnce(() => import('./screens/Taste'))) // features 10/16: Your taste (flag taste_space)
const Category = named(loadCategory, 'default')
const RowPage = named(loadCategory, 'RowPage')
const ListingLink = named(loadCategory, 'ListingLink')
const MoodPage = named(loadCategory, 'MoodPage')
const Saved = named(loadSaved, 'default')
const Itineraries = named(loadSaved, 'Itineraries')
const BuildWho = named(loadBuild, 'BuildWho')
const BuildWhere = named(loadBuild, 'BuildWhere')
const BuildPick = named(loadBuild, 'BuildPick')
const BuildDay = named(loadBuild, 'BuildDay')
const BuildDayView = named(loadBuild, 'BuildDayView')
const BuildFrom = named(loadBuild, 'BuildFrom')
const ItineraryDay = named(loadItinerary, 'default')
const PlanRedirect = named(loadItinerary, 'PlanRedirect')
const FirstRun = named(loadFirstRun, 'default')
const Settings = named(loadSettings, 'default')
const loadGroups = reloadOnce(() => import('./screens/Groups'))
const GroupsList = named(loadGroups, 'GroupsList')
const GroupNew = named(loadGroups, 'GroupNew')
const GroupPage = named(loadGroups, 'GroupPage')
const GroupJoin = named(loadGroups, 'GroupJoin')
const Found = lazy(reloadOnce(() => import('./screens/Found')))
const FeedbackLink = lazy(reloadOnce(() => import('./screens/FeedbackLink')))
const Privacy = lazy(reloadOnce(() => import('./screens/Privacy')))
const Terms = lazy(reloadOnce(() => import('./screens/Terms')))
const Weekend = lazy(reloadOnce(() => import('./screens/Weekend')))
const ComeWith = lazy(reloadOnce(() => import('./screens/ComeWith')))
// Round 3, Lane 4: the pages a message's links open (not flag-gated: a link someone already got keeps working)
const loadMessageLink = reloadOnce(() => import('./screens/MessageLink'))
const Unsubscribe = named(loadMessageLink, 'Unsubscribe')
const VerifyContact = named(loadMessageLink, 'Verify')
const HowWasIt = lazy(reloadOnce(() => import('./screens/HowWasIt')))
const HostSubmit = lazy(reloadOnce(() => import('./screens/HostSubmit')))
const HostReview = lazy(reloadOnce(() => import('./screens/HostReview')))
const Trip = lazy(reloadOnce(() => import('./screens/Trip'))) // Round 3 Lane 6
function useWarmScreens() {
  useEffect(() => {
    // plain imports here: a failed background fetch (offline, mid-deploy) must never reload the page
    const warm = () => { for (const load of WARM) void load().catch(() => {}) }
    const w = window as Window & { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number }
    let t = 0
    const start = () => { t = window.setTimeout(() => (w.requestIdleCallback ? w.requestIdleCallback(warm, { timeout: 3000 }) : warm()), 1500) }
    if (document.readyState === 'complete') start()
    else window.addEventListener('load', start, { once: true })
    return () => { window.clearTimeout(t); window.removeEventListener('load', start) }
  }, [])
}

function ScrollToTop() {
  const { pathname } = useLocation()
  const nav = useNavigationType()
  useEffect(() => {
    // Back / Forward keep their place (Browse restores its own scroll); a new page starts at the top
    if (nav !== 'POP') window.scrollTo(0, 0)
  }, [pathname]) // eslint-disable-line react-hooks/exhaustive-deps
  return null
}

export default function App() {
  // iOS Safari only applies :active (our press feedback) when a touch listener exists.
  useEffect(() => {
    const noop = () => {}
    document.addEventListener('touchstart', noop, { passive: true })
    return () => document.removeEventListener('touchstart', noop)
  }, [])

  // R-52: back from Google with a merge still to finish, no screen may run as the new account (lib/signin.ts)
  const signinGate = useSignInGate()
  useWarmScreens()
  const qaOn = useQaGate()
  // R4 Golden hour (flag golden_hour, on since 10/17): index.css swaps the tokens, corners and title face under html[data-look].
  // index.html ships data-look="golden" so the first paint is already warm (no old-look frame); this keeps it in step with the flag.
  const golden = useFlag('golden_hour')
  useLayoutEffect(() => {
    if (golden) document.documentElement.setAttribute('data-look', 'golden')
    else document.documentElement.removeAttribute('data-look')
  }, [golden])

  return (
    <MotionConfig reducedMotion="user">
      <BrowserRouter>
        <ScrollToTop />
        <OfflineBanner />
        {signinGate ? <ScreenLoading /> : <Suspense fallback={<ScreenLoading />}>
          <Routes>
            {/* V2: first run before Browse for a new person; Browse first for everyone after */}
            <Route path="/" element={<FirstRunGate><Browse /></FirstRunGate>} />
            <Route path="/first-run" element={<FirstRun />} />
            <Route path="/category/:slug" element={<Category />} />
            <Route path="/row/:key" element={<RowPage />} />
            <Route path="/mood/:key" element={<MoodPage />} />
            <Route path="/listing/:id" element={<ListingLink />} />
            {/* V2: retired from navigation; old links land on Browse (Retake now reopens the first run) */}
            {['/welcome', '/start', '/portrait', '/tune', '/picks', '/explore', '/home', '/invite'].map((p) => <Route key={p} path={p} element={<Navigate to="/" replace />} />)}
            {/* Plan v3 (R-73): Save -> Saved by city -> build a one-day itinerary from a day or an activity */}
            <Route path="/saved" element={<Saved />} />
            <Route path="/build" element={<BuildWho />} />
            {/* lane D's group page "Build plan": /build/new?group=<id> lands on Pick a day with that group chosen */}
            <Route path="/build/new" element={<BuildWho />} />
            <Route path="/build/where" element={<BuildWhere />} />
            <Route path="/build/pick" element={<BuildPick />} />
            <Route path="/build/day" element={<BuildDay />} />
            <Route path="/build/day/:ymd" element={<BuildDayView />} />
            <Route path="/build/from/:itemId" element={<BuildFrom />} />
            <Route path="/itineraries" element={<Itineraries />} />
            <Route path="/itinerary/:id" element={<ItineraryDay />} />
            {/* Round 3 Lane 6 (R-42): trips across cities (flag trips) */}
            <Route path="/trip/new" element={<Trip />} />
            <Route path="/trip/:id" element={<Trip />} />
            {/* R-29 groups: the Groups area lives on Saved; a group page, the list, start one, and the invite link */}
            <Route path="/groups" element={<GroupsList />} />
            <Route path="/groups/new" element={<GroupNew />} />
            <Route path="/g/join/:code" element={<GroupJoin />} />
            <Route path="/g/:id" element={<GroupPage />} />
            {/* Plan v3: the draft plan, Create a plan and Your plans are gone; their links land where the thing lives now */}
            <Route path="/plan" element={<Navigate to="/saved" replace />} />
            <Route path="/plan/new" element={<Navigate to="/build" replace />} />
            <Route path="/plan/:id/menu" element={<PlanRedirect />} />
            <Route path="/plan/:id" element={<PlanRedirect />} />
            <Route path="/p/:token" element={<SharedPlan />} />
            <Route path="/found" element={<Found />} />
            <Route path="/host" element={<HostSubmit />} />
            <Route path="/host/review" element={<HostReview />} />
            {/* Plan v3: the old evening and couples doors have no screen any more */}
            {['/night/:id', '/n/:token', '/join/:code'].map((p) => <Route key={p} path={p} element={<Navigate to="/" replace />} />)}
            <Route path="/settings" element={<Settings />} />
            <Route path="/taste" element={<Taste />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/f/:addId" element={<FeedbackLink />} />
            {/* features 10/16 (growth lane): This weekend, for you; Come with me? */}
            <Route path="/weekend" element={<FirstRunGate><Weekend /></FirstRunGate>} />
            <Route path="/c/:token" element={<ComeWith />} />
            <Route path="/unsub" element={<Unsubscribe />} />
            <Route path="/verify" element={<VerifyContact />} />
            <Route path="/how" element={<HowWasIt />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>}
        <SignInHost />
        {REVIEW_KEY && <Suspense fallback={null}><ReviewLayer reviewKey={REVIEW_KEY} /></Suspense>}
        {qaOn && <Suspense fallback={null}><QAPanel /></Suspense>}
      </BrowserRouter>
    </MotionConfig>
  )
}
